<!--
 * @FileDescription: Terms & Conditions Page
 * @Author: jianguo Wang
 * @Date: 2025-01-07
-->
<template>
  <div class="page-wrapper">
    <headerPart />
    <main class="main-content">
      <div class="content-container">
        <router-link to="/" class="back-link">
          <svg class="back-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M19 12H5M12 19l-7-7 7-7" />
          </svg>
          Back to Home
        </router-link>

        <h1 class="page-title">Terms & Conditions</h1>
        <h2 class="page-subtitle">Apexis Terms of Use</h2>

        <div class="content-card">
          <!-- INTRODUCTION -->
          <section class="content-section">
            <h3 class="section-title">1. INTRODUCTION</h3>
            <div class="section-content">
              <p class="clause-item">
                <span class="clause-number">(1.1)</span> These Terms of Use ("Terms") govern your access to and use of
                this website ("Website") which is owned, administered and maintained by Apexis.
              </p>
              <p class="clause-item">
                <span class="clause-number">(1.2)</span> The terms "we", "us" and "our" also refer to Apexis. The
                term "you" refers to the person visiting the Website.
              </p>
              <p class="clause-item">
                <span class="clause-number">(1.3)</span> Please read these Terms carefully. They contain important
                information regarding your legal rights, including limitations on Apexis's and certain third parties'
                liability, disclaimers of warranties and a submission to jurisdiction.
              </p>
              <p class="clause-item">
                <span class="clause-number">(1.4)</span> Each time you access the Website by any means, you
                irrevocably agree to comply with the version of the Terms posted on the Website at the time you access
                the Website. You may not use the Website if you do not agree to these Terms.
              </p>
              <p class="clause-item">
                <span class="clause-number">(1.5)</span> We may revise these Terms from time to time, without prior
                notice. You are bound by any changes to these Terms upon our posting of such changes on the Website. You
                should check these Terms every time you access or use the Website to make certain that you are aware of
                the most updated Terms.
              </p>
            </div>
          </section>

          <!-- ADVERTISING RIGHTS -->
          <section class="content-section">
            <h3 class="section-title">2. ADVERTISING RIGHTS</h3>
            <div class="section-content">
              <p class="clause-item">
                <span class="clause-number">(2.1)</span> We reserve the right to sell and display any advertising,
                attribution, links, promotional and distribution rights.
              </p>
              <p class="clause-item">
                <span class="clause-number">(2.2)</span> We will be entitled to retain all revenue generated from any
                sales of such advertising, attribution, links, or promotional or distribution rights.
              </p>
              <p class="clause-item">
                <span class="clause-number">(2.3)</span> Nothing in these Terms obligates or may be deemed to obligate
                us to sell or offer to sell any advertising, promotion, or distribution rights.
              </p>
            </div>
          </section>

          <!-- DISCLAIMERS AND LIMITATIONS OF LIABILITY -->
          <section class="content-section">
            <h3 class="section-title">3. DISCLAIMERS AND LIMITATIONS OF LIABILITY</h3>
            <div class="section-content">
              <p class="clause-item">
                <span class="clause-number">(3.1)</span> The content on the Website is strictly for informational
                purposes only.
              </p>
              <p class="clause-item">
                <span class="clause-number">(3.2)</span> Nothing on or in the Website shall constitute or be construed
                as an offering of any currency, security or any financial instrument or as investment advice or
                investment recommendations (such as recommendations as to whether to purchase a currency, security or
                instrument) by Apexis or a recommendation as to an investment strategy by Apexis.
              </p>
              <p class="clause-item">
                <span class="clause-number">(3.3)</span> Content on the Website should not be considered as
                information sufficient upon which to base an investment strategy.
              </p>
              <p class="clause-item">
                <span class="clause-number">(3.4)</span> No content on the Website is tailored to the specific needs
                of any individual, entity or group of individuals.
              </p>
              <p class="clause-item">
                <span class="clause-number">(3.5)</span> Apexis expresses no opinion as to the future or expected
                value of any currency, security or other interest.
              </p>
              <p class="clause-item">
                <span class="clause-number">(3.6)</span> Apexis does not explicitly or implicitly recommend or
                suggest any investment strategy of any kind.
              </p>
              <p class="clause-item">
                <span class="clause-number">(3.7)</span> Content on the Website may not be used as a basis for any
                financial product or other product without the express prior written consent of Apexis.
              </p>
              <p class="clause-item">
                <span class="clause-number">(3.8)</span> Apexis does not review the content on the Website for
                accuracy, completeness or reliability.
              </p>
              <p class="clause-item">
                <span class="clause-number">(3.9)</span> Apexis does not warrant or guarantee the accuracy,
                completeness, reliability, timeliness, security, availability or integrity of any content published on
                the Website.
              </p>
              <p class="clause-item">
                <span class="clause-number">(3.10)</span> Apexis disclaims all liabilities for any errors or other
                inaccuracies relating to the content on the Website.
              </p>
              <p class="clause-item">
                <span class="clause-number">(3.11)</span> Apexis makes no representations about any content of the
                Website, or that the content of the Website will be uninterrupted or operated in combination with any
                software, service, system or other data or information. The content on the Website are provided on an
                "as is" basis without warranty of any kind.
              </p>
              <p class="clause-item">
                <span class="clause-number">(3.12)</span> Apexis disclaims all warranties and conditions that the
                Website is free of viruses or other harmful components.
              </p>
              <p class="clause-item">
                <span class="clause-number">(3.13)</span> Apexis hereby disclaims all warranties and conditions with
                regard to the content on the Website, including all implied warranties and conditions of
                merchantability, fitness for a particular purpose, title, and non-infringement.
              </p>
              <p class="clause-item">
                <span class="clause-number">(3.14)</span> To the maximum extent permitted by applicable law, Apexis
                assumes no liability or responsibility for any (i) errors, mistakes, or inaccuracies of any content on
                the Website; (ii) property damage, of any nature whatsoever, resulting from your access to or use of the
                Website; (iii) any unauthorised access to or use of the Website; (iv) any interruption or cessation of
                transmission to or from your access to the Website; (v) any bugs, viruses, trojan horses, or the like
                that may be transmitted to or through the use or access of the Website by any third party; (vi) any
                errors or omissions in any content or for any loss or damage incurred as a result of the use of any
                content posted, transmitted, or otherwise made available on the Website; and/or (vii) the defamatory,
                offensive, or illegal conduct of any third party.
              </p>
              <p class="clause-item">
                <span class="clause-number">(3.15)</span> In no event shall Apexis (and its respective officers,
                directors, employees, members, agents, and affiliates) be liable for any indirect, punitive, incidental,
                special, or consequential damages or damages for lost profits, arising out of, or in any way connected
                with, your access to or use of the Website or with the delay or inability to access, display, or use the
                Website; any computer viruses, information, software, linked services, products, and services obtained
                through the use or access of the Website; or otherwise arising out of the access to or use of the
                Website whether based on a theory of negligence, contract, tort, strict liability, or otherwise, and
                even if Apexis has been advised of the possibility of such damages.
              </p>
            </div>
          </section>

          <!-- INDEMNIFICATION -->
          <section class="content-section">
            <h3 class="section-title">4. INDEMNIFICATION</h3>
            <div class="section-content">
              <p class="clause-item">
                <span class="clause-number">(4.1)</span> Except to the extent prohibited under applicable law, you
                shall indemnify, defend and hold harmless Apexis and its members, managers, directors, officers,
                employees, partners, consultants, contractors, service providers, agents, affiliates, successors and
                assigns from and against any and all suits, actions, proceedings and claims by third parties (whether
                threatened or actual), and all losses, liabilities, damages, judgments, costs and expenses (including
                reasonable legal fees) arising out of, relating to or in connection with: (i) your use (or misuse) of
                and access to the Website; (ii) your violation of any of these Terms; (iii) your violation of any
                applicable law, rule or regulation arising out of your access or use of the Website; (d) any claim that
                any information provided by you to Apexis in connection with the Website caused damage to, infringed
                upon, misappropriated or otherwise violated the rights of any third party, including infringement,
                misappropriation or other violation of third-party intellectual property rights, or violation of any
                right of privacy or publicity; and/or (iv) any dispute that you have with any third party relating to or
                in connection with the Website.
              </p>
              <p class="clause-item">
                <span class="clause-number">(4.2)</span> Apexis reserves the right, at its own cost, to assume the
                exclusive defense and control of any matter otherwise subject to indemnification by you, in which event
                you will fully cooperate with Apexis in asserting any available defenses and in the conduct of such
                defense.
              </p>
            </div>
          </section>

          <!-- THIRD PARTY WEBSITES -->
          <section class="content-section">
            <h3 class="section-title">5. THIRD PARTY WEBSITES</h3>
            <div class="section-content">
              <p class="clause-item">
                <span class="clause-number">(5.1)</span> The Website may contain links to third-party websites. Your
                use of all links to third-party websites is at your own risk. We do not monitor or have any control
                over, and make no claim or representation regarding third-party websites.
              </p>
              <p class="clause-item">
                <span class="clause-number">(5.2)</span> To the extent such links are provided by us, they are
                provided only as a convenience, and a link to a third-party websites does not imply our endorsement,
                adoption or sponsorship of, or affiliation with, such third-party websites.
              </p>
              <p class="clause-item">
                <span class="clause-number">(5.3)</span> When you leave the Website, whether via a link contained on
                the Website or through the use of your web browser, mobile device or other navigational tool, the
                information you view is not provided by us.
              </p>
              <p class="clause-item">
                <span class="clause-number">(5.4)</span> These Terms do not govern your use of third-party websites.
                We are not responsible for, have no control over and do not monitor or review the content of any other
                website.
              </p>
              <p class="clause-item">
                <span class="clause-number">(5.5)</span> A link to a third-party website does not imply sponsorship,
                approval, affiliation or endorsement by Apexis of the linked third-party website or of that third
                party's products or services.
              </p>
              <p class="clause-item">
                <span class="clause-number">(5.6)</span> You agree that Apexis will not, under any circumstances, be
                responsible or liable, directly or indirectly, for any goods, services, information, resources and/or
                content available on or through any third-party website or services, for any dealings or communications
                you may have with third parties, or for any harm, damages or loss caused or alleged to be caused by or
                in connection with any of the foregoing or your use of or reliance on the materials or the content or
                business practices of any third party.
              </p>
            </div>
          </section>

          <!-- MODIFICATION OF THE WEBSITE -->
          <section class="content-section">
            <h3 class="section-title">6. MODIFICATION OF THE WEBSITE</h3>
            <div class="section-content">
              <p class="clause-item">
                <span class="clause-number">(6.1)</span> At any time and in Apexis's sole and absolute discretion,
                Apexis may (in whole or in part) modify, suspend or discontinue the Website without prior notice, for
                any reason whatsoever.
              </p>
              <p class="clause-item">
                <span class="clause-number">(6.2)</span> We shall have no liability to you or to any third party for
                any such modification, suspension or discontinuance of the Website.
              </p>
            </div>
          </section>

          <!-- ENFORCEMENT -->
          <section class="content-section">
            <h3 class="section-title">7. ENFORCEMENT</h3>
            <div class="section-content">
              <p class="clause-item">
                <span class="clause-number">(7.1)</span> The remedies available to Apexis in these Terms are
                cumulative and in addition to any others available to Apexis.
              </p>
              <p class="clause-item">
                <span class="clause-number">(7.2)</span> Apexis may seek all remedies available to it at law and in
                equity for any violation of these Terms.
              </p>
              <p class="clause-item">
                <span class="clause-number">(7.3)</span> Apexis may suspend, terminate or block your access to the
                Website (in whole or in part) for any violation or suspected violation as we determine, without notice
                to you.
              </p>
              <p class="clause-item">
                <span class="clause-number">(7.4)</span> Your violation of these Terms shall be considered a breach of
                contract.
              </p>
              <p class="clause-item">
                <span class="clause-number">(7.5)</span> We reserve the right, but do not assume any obligation, to
                investigate any suspected violation of these Terms or any misuse of the Website.
              </p>
              <p class="clause-item">
                <span class="clause-number">(7.6)</span> In addition, we further reserve the right to report any
                activity, data or persons to, and otherwise cooperate with: (i) law enforcement authorities; (ii)
                financial regulators; (iii) system administrators at Internet service providers, networks or computing
                facilities; and (iv) providers and/or third-party vendors if we suspect that you have violated these
                Terms or any law, rule or regulation.
              </p>
              <p class="clause-item">
                <span class="clause-number">(7.7)</span> You acknowledge that such reporting or cooperation may
                include, without limitation, providing information relating to you and/or your use of the Website,
                including without limitation your email address, IP address or other identifying information, to law
                enforcement authorities, financial regulators, third-party providers, vendors or system administrators.
              </p>
              <p class="clause-item">
                <span class="clause-number">(7.8)</span> Further, we may disclose any information we think necessary
                to comply with applicable law, regulation, subpoena or other legal process or governmental or regulatory
                request.
              </p>
            </div>
          </section>

          <!-- GOVERNING LAW -->
          <section class="content-section">
            <h3 class="section-title">8. GOVERNING LAW; SUBMISSION TO JURISDICTION</h3>
            <div class="section-content">
              <p class="clause-item">
                <span class="clause-number">(8.1)</span> These Terms and any and all claims, disputes or other legal
                proceedings by or between you and us, including but not limited to any claims or disputes that are in
                any way related to or arising out of these Terms or your use of or access to the Website, shall be
                governed by and construed in accordance with the law of Singapore, without regard to any principles of
                conflicts of law.
              </p>
              <p class="clause-item">
                <span class="clause-number">(8.2)</span> You agree that all disputes, controversies or claims between
                the Parties arising out of or in connection with this Agreement (including its existence, validity or
                termination) shall be finally resolved by the courts in Singapore.
              </p>
            </div>
          </section>

          <!-- GENERAL -->
          <section class="content-section">
            <h3 class="section-title">9. GENERAL</h3>
            <div class="section-content">
              <p class="clause-item">
                <span class="clause-number">(9.1)</span> These Terms constitute the entire agreement between you and
                Apexis relating to your use of the Website and supersede all prior or contemporaneous communications,
                whether electronic, oral or written, between you and Apexis with respect to the Website.
              </p>
              <p class="clause-item">
                <span class="clause-number">(9.2)</span> You agree that no joint venture, agency, partnership, or
                employment relationship exists between you and Apexis and/or its affiliates as a result of these Terms
                or use of the Website.
              </p>
              <p class="clause-item">
                <span class="clause-number">(9.3)</span> In no event shall Apexis be responsible or liable for any
                failure or delay in the performance of its obligations hereunder arising out of or caused by, directly
                or indirectly, forces beyond its control.
              </p>
              <p class="clause-item">
                <span class="clause-number">(9.4)</span> Our performance under these Terms is subject to existing laws
                and regulations, and nothing contained in these Terms limits our right to comply with law enforcement or
                other governmental or legal requests or requirements relating to your use of the Website or information
                provided to, or gathered by, us with respect to such use.
              </p>
              <p class="clause-item">
                <span class="clause-number">(9.5)</span> If any provision of these Terms, including, but not limited
                to, the warranty disclaimers and limitations of liability set forth above, is determined to be invalid
                or unenforceable under applicable law, the invalid or unenforceable provisions in these Terms shall be
                deemed superseded by valid and enforceable provisions that, to the extent possible, fulfil the business
                purposes and intent of such invalid and unenforceable provisions.
              </p>
              <p class="clause-item">
                <span class="clause-number">(9.6)</span> Unless expressly provided to the contrary in these Terms, a
                person who is not a party to these Terms has no rights at law and in equity to enforce or enjoy the
                benefit of any term in these Terms.
              </p>
              <p class="clause-item">
                <span class="clause-number">(9.7)</span> All rights not granted herein are expressly reserved to
                Apexis.
              </p>
            </div>
          </section>
        </div>
      </div>
    </main>
    <footerPart />
  </div>
</template>

<script>
import headerPart from "../components/header.vue";
import footerPart from "../components/footer.vue";

export default {
  name: "TermsPage",
  components: {
    headerPart,
    footerPart,
  },
};
</script>

<style scoped>
.page-wrapper {
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}

.main-content {
  flex: 1;
  background: #f9fafb;
}

.content-container {
  max-width: 1024px;
  margin: 0 auto;
  padding: 48px 24px;
  text-align: left;
}

.back-link {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #4b5563;
  text-decoration: none;
  transition: color 0.2s;
  margin-bottom: 32px;
}

.back-link:hover {
  color: #8b3a3a;
}

.back-icon {
  width: 16px;
  height: 16px;
}

.page-title {
  font-size: 30px;
  font-weight: 700;
  color: #111827;
  margin-bottom: 8px;
  text-align: left;
}

.page-subtitle {
  font-size: 20px;
  font-weight: 500;
  color: #8b3a3a;
  margin-bottom: 32px;
  text-align: left;
}

.content-card {
  background: #ffffff;
  border-radius: 8px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 32px;
  text-align: left;
}

.content-section {
  display: flex;
  flex-direction: column;
}

.section-title {
  font-size: 18px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 16px;
  padding-bottom: 8px;
  border-bottom: 1px solid #e5e7eb;
}

.section-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.clause-item {
  font-size: 14px;
  color: #374151;
  line-height: 1.6;
}

.clause-number {
  font-weight: 500;
  margin-right: 4px;
}

@media screen and (max-width: 768px) {
  .content-container {
    padding: 32px 16px;
  }

  .content-card {
    padding: 24px;
  }

  .page-title {
    font-size: 24px;
  }

  .page-subtitle {
    font-size: 18px;
  }

  .section-title {
    font-size: 16px;
  }

  .clause-item {
    font-size: 13px;
  }
}
</style>
